import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from "firebase/auth";
import { auth, isFirebasePhoneAuthEnabled } from "./config";

let recaptchaVerifier = null;
let confirmationResult = null;
let sendInProgress = false;

/** Stable parent in index.html — never remove/replace this node */
const RECAPTCHA_ANCHOR_ID = "recaptcha-anchor";

const isVisibleRecaptchaEnabled = () =>
  process.env.REACT_APP_FIREBASE_RECAPTCHA_VISIBLE === "true";

export const formatPhoneE164 = (phoneCode, phoneNumber) => {
  const code = String(phoneCode).replace(/\D/g, "");
  let number = String(phoneNumber).replace(/\D/g, "");
  if (number.startsWith("0")) {
    number = number.replace(/^0+/, "");
  }
  return `+${code}${number}`;
};

export const getOtpDigitCount = () =>
  isFirebasePhoneAuthEnabled() ? 6 : 4;

const getRecaptchaAnchor = () => {
  const anchor = document.getElementById(RECAPTCHA_ANCHOR_ID);
  if (!anchor) {
    throw new Error("RECAPTCHA_CONTAINER_MISSING");
  }
  return anchor;
};

const waitForDomCleanup = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 150));
  });

/** Append a fresh child under the stable anchor (avoids null .style on removed nodes) */
const mountFreshRecaptchaHost = () => {
  const anchor = getRecaptchaAnchor();
  anchor.replaceChildren();

  const host = document.createElement("div");
  host.className = isVisibleRecaptchaEnabled()
    ? "recaptcha-visible"
    : "recaptcha-host";
  anchor.appendChild(host);
  return host;
};

export const resetRecaptcha = async () => {
  if (recaptchaVerifier) {
    try {
      await recaptchaVerifier.clear();
    } catch (_) {
      /* ignore */
    }
    recaptchaVerifier = null;
  }
  getRecaptchaAnchor().replaceChildren();
  await waitForDomCleanup();
};

export const initRecaptcha = async () => {
  if (!isFirebasePhoneAuthEnabled()) {
    throw new Error("Firebase phone auth is not enabled");
  }

  await resetRecaptcha();

  const host = mountFreshRecaptchaHost();
  const size = isVisibleRecaptchaEnabled() ? "normal" : "invisible";

  recaptchaVerifier = new RecaptchaVerifier(auth, host, {
    size,
    callback: () => {},
    "expired-callback": () => {
      recaptchaVerifier = null;
    },
  });

  return recaptchaVerifier;
};

const logFirebaseAuthError = (error, context) => {
  if (process.env.NODE_ENV === "development") {
    console.error(`[Firebase Auth: ${context}]`, {
      code: error?.code,
      message: error?.message,
      customData: error?.customData,
    });
  }
};

export const sendPhoneOtp = async (phoneE164) => {
  if (sendInProgress) {
    const err = new Error("SEND_IN_PROGRESS");
    err.code = "auth/too-many-requests";
    throw err;
  }

  if (!/^\+[1-9]\d{7,14}$/.test(phoneE164)) {
    const err = new Error("INVALID_E164");
    err.code = "auth/invalid-phone-number";
    throw err;
  }

  sendInProgress = true;

  try {
    const verifier = await initRecaptcha();
    confirmationResult = await signInWithPhoneNumber(
      auth,
      phoneE164,
      verifier
    );
    return confirmationResult;
  } catch (error) {
    logFirebaseAuthError(error, "sendPhoneOtp");
    await resetRecaptcha();
    throw error;
  } finally {
    sendInProgress = false;
  }
};

export const verifyPhoneOtpCode = async (code) => {
  if (!confirmationResult) {
    throw new Error("NO_CONFIRMATION");
  }
  const credential = await confirmationResult.confirm(code);
  return credential.user;
};

export const resendPhoneOtp = async (phoneE164) => {
  confirmationResult = null;
  return sendPhoneOtp(phoneE164);
};

export const clearPhoneAuthSession = () => {
  confirmationResult = null;
  resetRecaptcha();
};

export const getFirebaseIdToken = async () => {
  const user = auth?.currentUser;
  if (!user) return null;
  return user.getIdToken();
};

const FIREBASE_ERROR_KEYS = {
  "auth/invalid-phone-number": "phone_not_valid",
  "auth/too-many-requests": "firebase_too_many_requests",
  "auth/code-expired": "OtpVerify",
  "auth/invalid-verification-code": "OtpVerify",
  "auth/missing-verification-code": "otp_req",
  "auth/captcha-check-failed": "firebase_recaptcha_failed",
  "auth/invalid-app-credential": "firebase_invalid_app_credential",
  "auth/internal-error": "firebase_internal_error",
  "auth/network-request-failed": "firebase_network_error",
  "auth/operation-not-allowed": "firebase_operation_not_allowed",
  "auth/app-not-authorized": "firebase_app_not_authorized",
  "auth/quota-exceeded": "firebase_quota_exceeded",
  RECAPTCHA_CONTAINER_MISSING: "firebase_recaptcha_failed",
  NO_CONFIRMATION: "firebase_session_expired",
  "Firebase phone auth is not enabled": "firebase_not_configured",
};

export const mapFirebaseAuthError = (error) => {
  const code = error?.code || "";
  if (FIREBASE_ERROR_KEYS[code]) {
    return FIREBASE_ERROR_KEYS[code];
  }

  const message = String(error?.message || "").toLowerCase();

  if (
    message.includes("cannot read properties of null") ||
    message.includes("already been rendered")
  ) {
    return "firebase_recaptcha_failed";
  }
  if (
    message.includes("recaptcha enterprise") ||
    message.includes("failed to initialize recaptcha")
  ) {
    return "firebase_recaptcha_verifying";
  }
  if (message.includes("recaptcha") || message.includes("captcha")) {
    return "firebase_recaptcha_failed";
  }
  if (message.includes("network")) {
    return "firebase_network_error";
  }

  return "something_went_wrong";
};

export const getFirebaseAuthErrorText = (getAppText, error) => {
  const key = mapFirebaseAuthError(error);
  return getAppText(key) || getAppText("something_went_wrong");
};
